// Inline-expand variant of the Deliverable form — wraps DeliverableFormFields
// in a write-mode container rendered inside an expanded TrackingTable row.
// Auto-focuses the first input on mount; calls `onClosed` on unmount so
// the parent can restore focus to the row's toggle button. Uses
// toUpdateDeliverableInput (not toCreateDeliverableInput) on the save path
// so mode-gated fields (ragRating on parent rows, etc.) are omitted from the
// payload rather than silently cleared. In edit mode the lifecycle log editor
// is rendered; when lifecycleEventsReplace is in state the save path sends
// that field and omits status and lifecycleEvents (delta append). When the
// RAG lock is active (overdue/late) ragRating is omitted from the payload so
// the lock's Red is never persisted as a manual rating.

import { useEffect, useRef, useState } from "react";
import Button from "../../../common/Button";
import Alert from "../../../common/Alert";
import DeliverableFormFields, {
  type DeliverableFormMode,
} from "./DeliverableFormFields";
import {
  buildSowIdUpdatePatch,
  stateFromDeliverable,
  toUpdateDeliverableInput,
  type DeliverableFieldErrors,
  type DeliverableFormState,
} from "./deliverableFormState";
import { deriveDeliverableStatus } from "./deriveDeliverableStatus";
import { isValidIsoDate } from "../shared/isoDateValidation";
import { validateLifecycleChain } from "./lifecycleTransitions";
import type {
  Deliverable,
  Milestone,
  Sow,
  UpdateDeliverableInput,
} from "../../../../types";

// Soft / hard defaults applied at mount (display only — see omitLockedRag
// in handleSubmit for what reaches the wire):
//  - overdue + no stored RAG → show Red (also locked at the field
//    level via DeliverableFormFields ragLocked).
//  - accepted + no stored RAG → default to Green (radios stay editable).
function applyRagDefaults(
  state: DeliverableFormState,
  item: Deliverable,
): DeliverableFormState {
  if (state.ragRating !== "") return state;
  const derived = deriveDeliverableStatus(
    item.status,
    item.dueDate,
    new Date(),
    item.submittedAt,
  );
  // Only an unsubmitted overdue row is seeded Red: that is what the server
  // derives for it on save. A late row (submitted after the due date) is
  // derived amber server-side, so seeding Red here would show a value the
  // save never produces.
  if (derived === "overdue") {
    return { ...state, ragRating: "red" };
  }
  if (item.status === "accepted") return { ...state, ragRating: "green" };
  return state;
}

function validate(state: DeliverableFormState): DeliverableFieldErrors {
  const errors: DeliverableFieldErrors = {};
  if (!state.name.trim()) errors.name = "Name is required";
  if (state.dueDate && !isValidIsoDate(state.dueDate)) {
    errors.dueDate = "Enter a valid due date";
  }
  if (state.recurrenceRule) {
    if (!isValidIsoDate(state.recurrenceRule.startDate)) {
      errors.recurrenceStartDate = "Enter a valid recurrence start date";
    }
    if (
      state.recurrenceRule.endDate &&
      !isValidIsoDate(state.recurrenceRule.endDate)
    ) {
      errors.recurrenceEndDate = "Enter a valid recurrence end date";
    }
  }
  // Validate the lifecycle chain when the editor is active.
  if (state.lifecycleEventsReplace !== undefined) {
    const chain = validateLifecycleChain(state.lifecycleEventsReplace);
    if (!chain.ok) {
      errors.lifecycleChain = chain.error;
    }
  }
  return errors;
}

interface DeliverableInlineExpandProps {
  item: Deliverable;
  contractId: string;
  milestones?: Milestone[];
  /** SOWs on the parent contract. Threaded into `<DeliverableFormFields>`
   *  so the SOW picker can render available options and reflect the
   *  current attachment when editing. */
  sows?: Sow[];
  onUpdate: (input: UpdateDeliverableInput) => Promise<unknown>;
  onCancel: () => void;
  onClosed?: () => void;
  isSubmitting?: boolean;
  /**
   * When `"parent"`, the form renders metadata-only fields plus a
   * read-only recurrence preview — template-level recurrence edits
   * stay deferred behind the Generate Occurrences flow. Default lets
   * DeliverableFormFields auto-derive from the presence of a
   * recurrence rule.
   */
  mode?: DeliverableFormMode;
}

export default function DeliverableInlineExpand({
  item,
  contractId,
  milestones,
  sows,
  onUpdate,
  onCancel,
  onClosed,
  isSubmitting,
  mode,
}: DeliverableInlineExpandProps) {
  const [state, setState] = useState<DeliverableFormState>(() => {
    const formState = applyRagDefaults(stateFromDeliverable(item), item);
    // Seed the lifecycle log editor for every non-recurring deliverable — the
    // LifecycleLogEditor is the uniform edit surface and the legacy bare-
    // `status` write path is gone. Log-less rows (brand-new not-started) seed
    // an empty array: the replace path recomputes derived state from it with
    // nothing to lose. Recurring parents have no lifecycle of their own.
    if (mode !== "parent" && !item.recurrenceRule) {
      formState.lifecycleEventsReplace = [...(item.lifecycleEvents ?? [])];
    }
    return formState;
  });
  const ragLockedDerived = deriveDeliverableStatus(
    item.status,
    item.dueDate,
    new Date(),
    item.submittedAt,
  );
  const ragLocked =
    ragLockedDerived === "overdue" || ragLockedDerived === "late";
  const [errors, setErrors] = useState<DeliverableFieldErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const first = rootRef.current?.querySelector<HTMLElement>(
      'input:not([type="checkbox"]):not([type="radio"]), select, textarea',
    );
    first?.focus();
  }, []);

  useEffect(() => {
    return () => {
      onClosed?.();
    };
  }, [onClosed]);

  const handleChange = (patch: Partial<DeliverableFormState>) => {
    setState((prev) => ({ ...prev, ...patch }));
    const keys = Object.keys(patch);
    setErrors((prev) => {
      const next = { ...prev };
      for (const k of keys) {
        delete next[k as keyof DeliverableFieldErrors];
      }
      if (keys.includes("recurrenceRule")) {
        delete next.recurrenceStartDate;
        delete next.recurrenceEndDate;
      }
      return next;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const v = validate(state);
    if (Object.keys(v).length > 0) {
      setErrors(v);
      return;
    }
    setSubmitError(null);
    try {
      // isRecurringParent mirrors the same heuristic used in
      // DeliverableFormFields so hidden fields are omitted from the payload
      // rather than being sent as empty strings (which would silently clear
      // stored values on parent/occurrence rows).
      const isRecurringParent =
        mode === "parent" || (!mode && !!state.recurrenceRule);
      // The RAG lock shows Red on an overdue/late row and disables the
      // radios, so whatever the form state holds for ragRating is the
      // lock's value, not a choice the user made. It must not go on the
      // wire: the server would store it as a MANUAL rating and then never
      // derive the late-submission amber or the acceptance green over it.
      // The server derives the overdue red itself. A rating the user chose
      // while the radios were still editable (not locked at open) is a
      // genuine manual choice and is sent as usual.
      const omitLockedRag = (
        patch: UpdateDeliverableInput,
      ): UpdateDeliverableInput => {
        if (!ragLocked) return patch;
        const { ragRating: _locked, ...rest } = patch;
        void _locked;
        return rest;
      };
      let input: UpdateDeliverableInput;

      if (state.lifecycleEventsReplace !== undefined) {
        // Lifecycle-replace path: send lifecycleEventsReplace + non-status
        // fields. The backend rejects a body carrying both
        // lifecycleEventsReplace and a bare status; `toUpdateDeliverableInput`
        // no longer emits status (the legacy write path is gone), so the strip
        // below is defensive.
        const base = toUpdateDeliverableInput(state, item, isRecurringParent);
        const { status: _s, lifecycleEvents: _lc, ...rest } = base;
        void _s; void _lc;
        input = omitLockedRag({
          version: item.version,
          contractId,
          ...rest,
          ...buildSowIdUpdatePatch(state, item),
          lifecycleEventsReplace: state.lifecycleEventsReplace,
        });
      } else {
        // Recurring-parent edit: no lifecycle of its own, no per-occurrence
        // fields.
        input = omitLockedRag({
          version: item.version,
          contractId,
          ...toUpdateDeliverableInput(state, item, isRecurringParent),
          ...buildSowIdUpdatePatch(state, item),
        });
      }

      await onUpdate(input);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to save deliverable.";
      // 409 version conflict — surface a reload prompt.
      if (msg.toLowerCase().includes("version conflict") || msg.includes("409")) {
        setSubmitError(
          "This deliverable was modified by another user. Reload and try again.",
        );
      } else {
        setSubmitError(msg);
      }
    }
  };

  return (
    <div ref={rootRef} data-testid="deliverable-inline-expand">
      <form onSubmit={handleSubmit} className="space-y-4">
        <DeliverableFormFields
          value={state}
          onChange={handleChange}
          errors={errors}
          idPrefix={`del-inline-${item.id}`}
          disabled={isSubmitting}
          milestones={milestones}
          sows={sows}
          mode={mode}
          ragLocked={ragLocked}
          isEditMode
          sourceLifecycleEvents={item.lifecycleEvents}
        />
        {submitError && <Alert variant="error">{submitError}</Alert>}
        <div className="flex justify-end gap-3">
          <Button
            variant="secondary"
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            loading={isSubmitting}
            disabled={isSubmitting}
          >
            Save changes
          </Button>
        </div>
      </form>
    </div>
  );
}
